/**
 * Funciones puras de formato para celdas de DataTable.
 * Spec: docs/architecture/COLUMN_CONFIG_PROPOSAL.md
 *
 * Idioma de formato: es-AR por default (separador miles "." y decimal ",")
 * salvo currency USD que usa formato en-US ("," miles, "." decimal).
 */

export type FormatType =
  | 'text'
  | 'number'
  | 'percentage'
  | 'currency'
  | 'date'
  | 'boolean';

export interface ColumnFormat {
  format: FormatType;
  decimals?: number;
  thousandsSeparator?: boolean;
  currencySymbol?: string;
}

/**
 * Devuelve true si value es null, undefined, '', o NaN.
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;
  return false;
}

/**
 * Convierte un valor a número o NaN.
 */
function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const cleaned = v.replace(/\s/g, '').replace(/,/g, '.');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

/**
 * Format number con decimales fijos y separador de miles configurable.
 * Locale: es-AR (1.234,56) cuando thousandsSeparator=true.
 */
export function formatNumber(
  value: unknown,
  decimals: number = 0,
  thousandsSeparator: boolean = true
): string {
  if (isEmpty(value)) return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  const opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: thousandsSeparator,
  };
  return new Intl.NumberFormat('es-AR', opts).format(n);
}

/**
 * Format porcentaje. El valor numérico ya está en escala 0-100 (no se multiplica).
 * Ejemplo: 25 → "25%"
 */
export function formatPercentage(
  value: unknown,
  decimals: number = 0,
  thousandsSeparator: boolean = false
): string {
  if (isEmpty(value)) return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  return `${formatNumber(n, decimals, thousandsSeparator)}%`;
}

/**
 * Format moneda. Por default símbolo "$" + separador de miles + 2 decimales.
 * USD: "USD" usa formato en-US ($1,234.56). Resto usa es-AR ($1.234,56).
 */
export function formatCurrency(
  value: unknown,
  symbol: string = '$',
  decimals: number = 2,
  thousandsSeparator: boolean = true
): string {
  if (isEmpty(value)) return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return '—';
  const isUSD = symbol === 'USD' || symbol === 'US$';
  const locale = isUSD ? 'en-US' : 'es-AR';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: thousandsSeparator,
  }).format(n);
  return `${symbol} ${formatted}`;
}

/**
 * Format fecha. Acepta Date, ISO string, o timestamp.
 * Output: dd/mm/yyyy (es-AR).
 */
export function formatDate(value: unknown): string {
  if (isEmpty(value)) return '—';
  let d: Date;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string') d = new Date(value);
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(d);
}

/**
 * Format booleano. Acepta bool, "true"/"false", 1/0, "Sí"/"No".
 */
export function formatBoolean(value: unknown): string {
  if (isEmpty(value)) return '—';
  if (value === true || value === 1 || value === '1') return 'Sí';
  if (value === false || value === 0 || value === '0') return 'No';
  if (typeof value === 'string') {
    const v = value.toLowerCase().trim();
    if (['true', 'yes', 'sí', 'si', 'y', 's'].includes(v)) return 'Sí';
    if (['false', 'no', 'n'].includes(v)) return 'No';
  }
  return '—';
}

/**
 * Dispatcher principal: aplica el format correcto según config.
 */
export function formatValue(value: unknown, fmt: ColumnFormat): string {
  if (!fmt || !fmt.format) return value == null ? '—' : String(value);
  switch (fmt.format) {
    case 'number':
      return formatNumber(value, fmt.decimals ?? 0, fmt.thousandsSeparator ?? true);
    case 'percentage':
      return formatPercentage(value, fmt.decimals ?? 0, fmt.thousandsSeparator ?? false);
    case 'currency':
      return formatCurrency(
        value,
        fmt.currencySymbol || '$',
        fmt.decimals ?? 2,
        fmt.thousandsSeparator ?? true
      );
    case 'date':
      return formatDate(value);
    case 'boolean':
      return formatBoolean(value);
    case 'text':
    default:
      if (isEmpty(value)) return '—';
      return String(value);
  }
}
